import { IPayrollReport } from "../../models/Payroll.ts";
import { calcularTiempoTrabajado } from "../../interfaces/formik/IReporteRelojChecador.ts";
import {
  isNightShiftCompleteSegment,
  isSplitDoubleShiftNightComplete,
} from "../../utils/payrollNightShiftRules.ts";
import {
  additionalMultiVisitExtraPay,
  appendMultiVisitExtrasToConcept,
  hasMultiplePayrollDayVisits,
  multiVisitDayExtraHours,
} from "../../utils/payrollMultiVisitExtras.ts";
import { getPartnerHoursByDate, chartHorasExtrasExcludingDoubleShift } from "../../utils/payrollUtils.ts";
import {
  hasAcceptedFreeDayWorkedIncident,
  withFreeDayWorkedPremium,
} from "./payPremiums.ts";

/** Valor numérico de horas para tablas de nómina (1 decimal). */
export function formatPayrollHoursDecimal(
  decimalHours: number | string
): string {
  const n = Number(decimalHours);
  if (!Number.isFinite(n)) {
    return "0.0";
  }
  return n.toFixed(1);
}

export function formatPayrollHoursWithH(
  decimalHours: number | string
): string {
  return `${formatPayrollHoursDecimal(decimalHours)}h`;
}

export function formatHours(decimalHours: number): string {
  return `${formatPayrollHoursDecimal(decimalHours)} horas`;
}

export function formatHoursToTime(decimalHours: number): string {
  const hours = Math.floor(decimalHours);
  const minutes = Math.round((decimalHours - hours) * 60);
  return `${hours.toString().padStart(2, "0")}:${minutes
    .toString()
    .padStart(2, "0")}`;
}

export function roundHours(hours: number, fullDayHours: number = 8.5): number {
  const integerPart = Math.floor(hours);
  const decimalPart = hours - integerPart;
  const threshold = fullDayHours - 0.09; // equivalente a 8.41 para 8.5
  const nextHour = fullDayHours + 0.5;

  if (hours < threshold) {
    return integerPart;
  }

  if (hours >= threshold && hours <= fullDayHours) {
    return fullDayHours;
  }

  if (hours > fullDayHours && hours < nextHour) {
    return fullDayHours;
  }

  if (hours >= nextHour && hours < nextHour + 0.5) {
    const decimalFromNext = hours - nextHour;

    if (decimalFromNext >= 0.4) {
      return nextHour + 0.5;
    } else {
      return nextHour;
    }
  }

  if (hours === nextHour + 0.5) {
    return nextHour + 0.5;
  }

  if (hours > nextHour + 0.5) {
    const extraHours = hours - fullDayHours;
    const extraIntegerPart = Math.floor(extraHours);
    const extraDecimalPart = extraHours - extraIntegerPart;

    let roundedExtraHours;
    if (extraDecimalPart >= 0.4) {
      roundedExtraHours = extraIntegerPart + 0.5;
    } else {
      roundedExtraHours = extraIntegerPart;
    }

    return fullDayHours + roundedExtraHours;
  }

  return hours;
}

export function parseCelebrityDayMultiplier(bonusType: string): number {
  const match = bonusType.match(/Salario x(\d+\.?\d*)/);
  return match ? parseFloat(match[1]) : 1;
}

function withMultiVisitExtraPay(
  basePay: number,
  schedule: any,
  totalHours: number,
  hoursInSchedule: number,
  hourlyPay: number,
  fullDayHours: number,
  NormalCalculationNomina: boolean
): number {
  const extraHours = additionalMultiVisitExtraPay(
    totalHours,
    hoursInSchedule,
    fullDayHours,
    NormalCalculationNomina,
    schedule,
    roundHours
  );
  return basePay + extraHours * hourlyPay;
}

export function calculateDailyPayment(
  schedule: any,
  dailySalary: number,
  hourlyPay: number,
  NormalCalculationNomina: boolean = false,
  hoursInSchedule: number = 0,
  fullDayHours: number = 8.5
) {
  if (
    !schedule.assistanceCheck_checkTime ||
    !schedule.assistanceCheckout_checkTime
  ) {
    return 0;
  }

  const hasAcceptedFreeDayIncident = hasAcceptedFreeDayWorkedIncident(
    schedule.incidents
  );

  const isCelebrityDayWorked =
    schedule.isCelebrityDayWorked && schedule.celebrityDay;
  const isNewSystem = schedule.celebrityDay?.isNewSystem || false;
  const holidayBonusAmount = schedule.celebrityDay?.amount;
  const celebrityMultiplier = isCelebrityDayWorked && !isNewSystem
    ? parseCelebrityDayMultiplier(schedule.celebrityDay.bonusType)
    : 1;

  const totalHours =
    schedule.registeredHoursOverride != null &&
    !Number.isNaN(Number(schedule.registeredHoursOverride))
      ? Number(schedule.registeredHoursOverride)
      : calcularTiempoTrabajado(
          schedule.assistanceCheck_checkTime,
          schedule.assistanceCheckout_checkTime
        );

  const hasScheduledTime =
    schedule.schedule_startTime && schedule.schedule_endTime;
  
  const hoursToUse = NormalCalculationNomina
    ? totalHours
    : hoursInSchedule > 0
    ? hoursInSchedule
    : totalHours;
  const roundedHours = roundHours(hoursToUse, fullDayHours);

  if (!hasScheduledTime) {
    if (roundedHours < fullDayHours) {
      return roundedHours * hourlyPay;
    } else {
      return dailySalary;
    }
  }

  const actualCheckoutTime = schedule.assistanceCheckout_checkTime;
  const hoursForNightRule = Math.max(hoursToUse, totalHours);
  const isNightShiftComplete = isNightShiftCompleteSegment(
    hoursForNightRule,
    actualCheckoutTime,
    fullDayHours
  );

  if (
    isSplitDoubleShiftNightComplete(
      totalHours,
      fullDayHours,
      actualCheckoutTime,
      schedule.visits,
      schedule.assistanceCheck_checkTime,
      actualCheckoutTime
    )
  ) {
    return 2 * dailySalary;
  }

  const doubleShiftThreshold = fullDayHours * 2;
  if (totalHours >= doubleShiftThreshold) {
    const totalHoursRounded = Math.floor(totalHours);
    const extraHours = totalHoursRounded - doubleShiftThreshold;
    return 2 * dailySalary + extraHours * hourlyPay;
  }

  if (hasAcceptedFreeDayIncident) {
    if (isNightShiftComplete) {
      return withFreeDayWorkedPremium(
        withMultiVisitExtraPay(
          dailySalary,
          schedule,
          totalHours,
          hoursInSchedule,
          hourlyPay,
          fullDayHours,
          NormalCalculationNomina
        )
      );
    }
    if (roundedHours < fullDayHours) {
      return withFreeDayWorkedPremium(roundedHours * hourlyPay);
    } else if (roundedHours === fullDayHours) {
      return withFreeDayWorkedPremium(dailySalary);
    } else {
      const extraHours = Math.floor(roundedHours - fullDayHours);
      return withFreeDayWorkedPremium(
        dailySalary + extraHours * hourlyPay
      );
    }
  }

  if (isCelebrityDayWorked) {
    // Para nuevos bonos, usar el monto directamente
    if (isNewSystem && holidayBonusAmount) {
      // El bono se suma al pago normal del día
      let basePayment = 0;
      if (isNightShiftComplete) {
        basePayment = dailySalary;
      } else if (roundedHours < fullDayHours) {
        basePayment = roundedHours * hourlyPay;
      } else if (roundedHours === fullDayHours) {
        basePayment = dailySalary;
      } else {
        const extraHours = Math.floor(roundedHours - fullDayHours);
        basePayment = dailySalary + extraHours * hourlyPay;
      }
      return basePayment + Number(holidayBonusAmount);
    }
    
    // Para legacy, usar multiplicador
    if (isNightShiftComplete) {
      return (
        withMultiVisitExtraPay(
          dailySalary,
          schedule,
          totalHours,
          hoursInSchedule,
          hourlyPay,
          fullDayHours,
          NormalCalculationNomina
        ) * celebrityMultiplier
      );
    }
    if (roundedHours < fullDayHours) {
      return roundedHours * hourlyPay * celebrityMultiplier;
    } else if (roundedHours === fullDayHours) {
      return (
        withMultiVisitExtraPay(
          dailySalary,
          schedule,
          totalHours,
          hoursInSchedule,
          hourlyPay,
          fullDayHours,
          NormalCalculationNomina
        ) * celebrityMultiplier
      );
    } else {
      const extraHours = Math.floor(roundedHours - fullDayHours);
      const celebrityHourlyPay = (dailySalary * celebrityMultiplier) / fullDayHours;
      return (
        dailySalary * celebrityMultiplier + extraHours * celebrityHourlyPay
      );
    }
  }

  if (isNightShiftComplete) {
    return withMultiVisitExtraPay(
      dailySalary,
      schedule,
      totalHours,
      hoursInSchedule,
      hourlyPay,
      fullDayHours,
      NormalCalculationNomina
    );
  }

  if (roundedHours < fullDayHours) {
    return roundedHours * hourlyPay;
  } else if (roundedHours === fullDayHours) {
    return withMultiVisitExtraPay(
      dailySalary,
      schedule,
      totalHours,
      hoursInSchedule,
      hourlyPay,
      fullDayHours,
      NormalCalculationNomina
    );
  } else if (roundedHours === fullDayHours + 0.5) {
    return withMultiVisitExtraPay(
      dailySalary,
      schedule,
      totalHours,
      hoursInSchedule,
      hourlyPay,
      fullDayHours,
      NormalCalculationNomina
    );
  } else {
    const extraHours = Math.floor(roundedHours - fullDayHours);
    return dailySalary + extraHours * hourlyPay;
  }
}

export function getDailyPaymentConcept(
  schedule: any,
  NormalCalculationNomina: boolean = false,
  hoursInSchedule: number = 0,
  fullDayHours: number = 8.5
) {
  if (
    !schedule.assistanceCheck_checkTime ||
    !schedule.assistanceCheckout_checkTime
  ) {
    return "Sin registro";
  }

  const totalHours =
    schedule.registeredHoursOverride != null &&
    !Number.isNaN(Number(schedule.registeredHoursOverride))
      ? Number(schedule.registeredHoursOverride)
      : calcularTiempoTrabajado(
          schedule.assistanceCheck_checkTime,
          schedule.assistanceCheckout_checkTime
        );

  const hasScheduledTime =
    schedule.schedule_startTime && schedule.schedule_endTime;
  
  const hoursToUse = NormalCalculationNomina
    ? totalHours
    : hoursInSchedule > 0
    ? hoursInSchedule
    : totalHours;
  const roundedHours = roundHours(hoursToUse, fullDayHours);

  if (!hasScheduledTime) {
    if (roundedHours < fullDayHours) {
      const displayHours = Math.floor(hoursToUse);
      return `${displayHours}h × pago por hora${
        NormalCalculationNomina ? " " : ""
      }`;
    } else {
      return `${fullDayHours}h (jornada completa)${NormalCalculationNomina ? " " : ""}`;
    }
  }

  const hasAcceptedFreeDayIncident = hasAcceptedFreeDayWorkedIncident(
    schedule.incidents
  );

  const isCelebrityDayWorked =
    schedule.isCelebrityDayWorked && schedule.celebrityDay;
  const isNewSystem = schedule.celebrityDay?.isNewSystem || false;
  const holidayBonusAmount = schedule.celebrityDay?.amount;
  const celebrityMultiplier = isCelebrityDayWorked && !isNewSystem
    ? parseCelebrityDayMultiplier(schedule.celebrityDay.bonusType)
    : 1;

  const actualCheckoutTime = schedule.assistanceCheckout_checkTime;
  const hoursForNightRule = Math.max(hoursToUse, totalHours);
  const isNightShiftComplete = isNightShiftCompleteSegment(
    hoursForNightRule,
    actualCheckoutTime,
    fullDayHours
  );

  if (hasAcceptedFreeDayIncident) {
    if (isNightShiftComplete) {
      return `${fullDayHours}h (salario diario + 30%) - Día libre trabajado (jornada completa por salida nocturna)${
        NormalCalculationNomina ? " " : ""
      }`;
    }
    if (roundedHours < fullDayHours) {
      const displayHours = Math.floor(hoursToUse);
      return `${displayHours}h × (pago por hora + 30%) - Día libre trabajado${
        NormalCalculationNomina ? " " : ""
      }`;
    } else if (roundedHours === fullDayHours) {
      return `${roundedHours}h (salario diario + 30%) - Día libre trabajado${
        NormalCalculationNomina ? " " : ""
      }`;
    } else if (roundedHours === fullDayHours + 0.5) {
      return `${fullDayHours}h (salario diario + 30%) - Día libre trabajado${
        NormalCalculationNomina ? " " : ""
      }`;
    } else {
      const extraHours = Math.floor(roundedHours - fullDayHours);
      if (extraHours > 0) {
        return `${roundedHours}h (base del día + 30%, incl. extras con +30%) - Día libre trabajado${
          NormalCalculationNomina ? " " : ""
        }`;
      } else {
        return `${fullDayHours}h (salario diario + 30%) - Día libre trabajado${
          NormalCalculationNomina ? " " : ""
        }`;
      }
    }
  }

  if (isCelebrityDayWorked) {
    // Para nuevos bonos, mostrar el monto directamente
    if (isNewSystem && holidayBonusAmount) {
      const bonusTitle = schedule.celebrityDay?.title || "Bono festivo";
      const bonusAmountFormatted = `$${Number(holidayBonusAmount).toFixed(2)}`;
      
      if (isNightShiftComplete) {
        return `${fullDayHours}h (${bonusAmountFormatted} - ${bonusTitle}) - Día festivo trabajado (jornada completa por salida nocturna)${
          NormalCalculationNomina ? " " : ""
        }`;
      }
      if (roundedHours < fullDayHours) {
        const displayHours = Math.floor(hoursToUse);
        return `${displayHours}h × pago por hora + ${bonusAmountFormatted} (${bonusTitle}) - Día festivo trabajado${
          NormalCalculationNomina ? " " : ""
        }`;
      } else {
        return `${roundedHours}h (${bonusAmountFormatted} - ${bonusTitle}) - Día festivo trabajado${
          NormalCalculationNomina ? " " : ""
        }`;
      }
    }
    
    // Para legacy, usar multiplicador
    if (isNightShiftComplete) {
      return `${fullDayHours}h (salario diario × ${celebrityMultiplier}) - Día festivo trabajado (jornada completa por salida nocturna)${
        NormalCalculationNomina ? " " : ""
      }`;
    }
    if (roundedHours < fullDayHours) {
      const displayHours = Math.floor(hoursToUse);
      return `${displayHours}h × (pago por hora × ${celebrityMultiplier}) - Día festivo trabajado${
        NormalCalculationNomina ? " " : ""
      }`;
    } else if (roundedHours === fullDayHours) {
      return `${roundedHours}h (salario diario × ${celebrityMultiplier}) - Día festivo trabajado${
        NormalCalculationNomina ? " " : ""
      }`;
    } else if (roundedHours === fullDayHours + 0.5) {
      return `${fullDayHours}h (salario diario × ${celebrityMultiplier}) - Día festivo trabajado${
        NormalCalculationNomina ? " " : ""
      }`;
    } else {
      const extraHours = Math.floor(roundedHours - fullDayHours);
      if (extraHours > 0) {
        return `${roundedHours}h (salario diario × ${celebrityMultiplier} + ${extraHours}h extras con multiplicador) - Día festivo trabajado${
          NormalCalculationNomina ? " " : ""
        }`;
      } else {
        return `${fullDayHours}h (salario diario × ${celebrityMultiplier}) - Día festivo trabajado${
          NormalCalculationNomina ? " " : ""
        }`;
      }
    }
  }

  if (isNightShiftComplete) {
    let concept = `${fullDayHours}h (jornada completa por salida nocturna)${
      NormalCalculationNomina ? " " : ""
    }`;
    if (hasMultiplePayrollDayVisits(schedule)) {
      const multiExtra = multiVisitDayExtraHours(totalHours, fullDayHours);
      if (multiExtra > 0) {
        concept = `${fullDayHours}h (jornada completa por salida nocturna) + ${multiExtra}h extras${
          NormalCalculationNomina ? " " : ""
        }`;
      }
    }
    return concept;
  }

  const doubleShiftMax = fullDayHours * 2;
  if (
    isSplitDoubleShiftNightComplete(
      totalHours,
      fullDayHours,
      actualCheckoutTime,
      schedule.visits,
      schedule.assistanceCheck_checkTime,
      actualCheckoutTime
    )
  ) {
    return `${doubleShiftMax}h (doble jornada por salida nocturna)${
      NormalCalculationNomina ? " " : ""
    }`;
  }

  const doubleShiftThreshold = fullDayHours * 2;
  if (totalHours >= doubleShiftThreshold) {
    const totalHoursRounded = Math.floor(totalHours);
    const extraHours = totalHoursRounded - doubleShiftThreshold;
    if (extraHours > 0) {
      return `${totalHoursRounded}h (doble jornada + ${extraHours}h extras)${NormalCalculationNomina ? " " : ""}`;
    } else {
      return `${totalHoursRounded}h (doble jornada)${
        NormalCalculationNomina ? " " : ""
      }`;
    }
  }

  if (roundedHours < fullDayHours) {
    const displayHours = Math.floor(hoursToUse);
    return `${displayHours}h × pago por hora${NormalCalculationNomina ? " " : ""}`;
  } else if (roundedHours === fullDayHours) {
    if (hasMultiplePayrollDayVisits(schedule)) {
      const multiExtra = multiVisitDayExtraHours(totalHours, fullDayHours);
      if (multiExtra > 0) {
        return `${fullDayHours}h (jornada completa + ${multiExtra}h extras)${
          NormalCalculationNomina ? " " : ""
        }`;
      }
    }
    return `${roundedHours}h (jornada completa)${
      NormalCalculationNomina ? " " : ""
    }`;
  } else if (roundedHours === fullDayHours + 0.5) {
    let concept = `${fullDayHours}h (jornada completa)${NormalCalculationNomina ? " " : ""}`;
    return appendMultiVisitExtrasToConcept(
      concept,
      totalHours,
      fullDayHours,
      schedule,
      NormalCalculationNomina
    );
  } else {
    const extraHours = Math.floor(roundedHours - fullDayHours);
    return `${roundedHours}h (jornada completa + ${extraHours}h extras)${
      NormalCalculationNomina ? " " : ""
    }`;
  }
}

export function calculateIncidentExtraHours(record: IPayrollReport): number {
  let totalExtraHours = 0;
  record.schedules.forEach((schedule) => {
    if (
      schedule.assistanceCheck_checkTime &&
      schedule.assistanceCheckout_checkTime
    ) {
      const startTime = schedule.schedule_startTime.split(" - ")[0] + ":00";
      const endTime = schedule.schedule_endTime.split(" - ")[1] + ":00";

      const checkOut = schedule.assistanceCheckout_checkTime;

      if (checkOut > endTime) {
        let extraTime = calcularTiempoTrabajado(endTime, checkOut);

        if (schedule.incidents && schedule.incidents.length > 0) {
          const hasRejectedIncidents = schedule.incidents.some(
            (incident: any) =>
              incident.status === "Salió tarde" && !incident.finalStatus
          );
          if (hasRejectedIncidents) {
            extraTime = 0;
          }
        }

        totalExtraHours += extraTime;
      }
    }
  });
  return totalExtraHours;
}

export function calculateExtraHoursLikeChart(
  detailedRecords: any[] | undefined,
  partnerId: number,
  authorizedSchedule?: Array<{ worked: number; day: string }>,
  fullDayHours: number = 8.5
): number {
  let total = 0;

  if (detailedRecords && detailedRecords.length > 0) {
    const partnerRecords = getPartnerHoursByDate(detailedRecords, partnerId);
    for (const rec of partnerRecords) {
      if (rec.hoursWorked > fullDayHours) {
        const extra = Math.floor(rec.hoursWorked - fullDayHours);
        total += chartHorasExtrasExcludingDoubleShift(
          rec.hoursWorked,
          extra,
          fullDayHours
        );
      }
    }
    return total;
  }

  if (authorizedSchedule && authorizedSchedule.length > 0) {
    for (const sched of authorizedSchedule) {
      const worked = Number(sched.worked || 0);
      if (worked > fullDayHours) {
        const extra = Math.floor(worked - fullDayHours);
        total += chartHorasExtrasExcludingDoubleShift(
          worked,
          extra,
          fullDayHours
        );
      }
    }
  }
  return total;
}
